import cn from 'classnames/bind';
import React, { FC, PropsWithChildren, useLayoutEffect, useRef } from 'react';
import ReactModal from 'react-modal';

import { CloseButton } from '@/shared/components/CloseButton/CloseButton';

import styles from './ContentModal.module.css';

const cx = cn.bind(styles);

type ElementColorScheme = 'dark' | 'light';

interface ContentModalProps {
    closeIcon?: boolean;
    id: string;
    overlayClassName?: string;
    className?: string;
    rounded?: boolean;
    isHideOnlyByCloseIcon?: boolean;
    colorScheme?: ElementColorScheme;
    onHideModal?: VoidFunction;
}

export const ContentModal: FC<PropsWithChildren<ContentModalProps>> = (props) => {
    const {
        children,
        closeIcon = true,
        id,
        overlayClassName,
        rounded = true,
        className,
        isHideOnlyByCloseIcon,
        colorScheme = 'dark',
        onHideModal,
    } = props;

    const overlayRef = useRef<HTMLDivElement | null>(null);

    const rootClassList = cx('root', `root_${colorScheme}`, className, {
        root_rounded: rounded,
        root_dark: rounded,
    });

    useLayoutEffect(() => {
        const oldBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = oldBodyOverflow;
        };
    }, []);

    useLayoutEffect(() => {
        const el = overlayRef.current;
        if (el) {
            el.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
    }, [id]);

    const setOverlayRef = React.useCallback((el: HTMLDivElement | null) => {
        (overlayRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    }, []);

    return (
        <ReactModal
            overlayRef={setOverlayRef}
            isOpen
            className={rootClassList}
            overlayClassName={cx('overlay', overlayClassName)}
            onRequestClose={!isHideOnlyByCloseIcon ? onHideModal : undefined}
        >
            <div className={cx('children-wrapper')}>
                {closeIcon && onHideModal && (
                    <div className={cx('close-button')}>
                        <CloseButton
                            onHandleClick={onHideModal}
                            colorScheme={colorScheme}
                            size={52}
                        />
                    </div>
                )}
                {children}
            </div>
        </ReactModal>
    );
};
